import { Activity, Ban, BookMarked, Wrench } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { CSSProperties, ReactNode } from "react";
import type { SlideDef, SlideProps } from "@/engine/types";
import { GradientText, Reveal, RevealItem, RevealList, SlideShell } from "@/ui";

function Strong({ children }: { children: ReactNode }) {
  return <span className="font-semibold text-white">{children}</span>;
}

/** Mono chip for the rarely-reached-for disavow tool. */
function ToolChip({ children }: { children: ReactNode }) {
  return (
    <span className="rounded-lg border border-white/15 bg-white/10 px-3 py-0.5 align-baseline font-mono text-2xl text-white/85">
      {children}
    </span>
  );
}

type Tone = "safe" | "danger" | "neutral";

interface Practice {
  icon: LucideIcon;
  tone: Tone;
  title: ReactNode;
  body: ReactNode;
}

const PRACTICES: readonly Practice[] = [
  {
    icon: BookMarked,
    tone: "safe",
    title: "Pitch assets people want to link to",
    body: (
      <>
        <Strong>Research</Strong>, <Strong>open data</Strong>,{" "}
        <Strong>small tools</Strong>, <Strong>original guides</Strong> — then{" "}
        <Strong>polite outreach</Strong> to relevant sites.
      </>
    ),
  },
  {
    icon: Ban,
    tone: "danger",
    title: "Avoid paid links, PBNs & spam",
    body: (
      <>
        Paid link networks, PBNs, and automated comment/forum spam are{" "}
        <Strong>high risk</Strong> — penalties, or simply ignored links.
      </>
    ),
  },
  {
    icon: Activity,
    tone: "neutral",
    title: "Track new links",
    body: (
      <>
        Watch them arrive in <Strong>Search Console (Links report)</Strong> or
        your SEO tool.
      </>
    ),
  },
  {
    icon: Wrench,
    tone: "neutral",
    title: (
      <>
        <ToolChip>disavow</ToolChip> is rare
      </>
    ),
    body: (
      <>
        Focus on <Strong>not building toxic links</Strong> in the first place.
      </>
    ),
  },
];

const CARD_STYLE: Record<Tone, CSSProperties | undefined> = {
  safe: {
    borderColor: "rgba(52,211,153,0.45)",
    boxShadow:
      "0 8px 32px rgba(0,0,0,0.35), 0 0 48px -10px rgba(52,211,153,0.3), inset 0 1px 0 rgba(255,255,255,0.08)",
  },
  danger: {
    borderColor: "rgba(251,113,133,0.55)",
    boxShadow:
      "0 8px 32px rgba(0,0,0,0.35), 0 0 48px -10px rgba(251,113,133,0.35), inset 0 1px 0 rgba(255,255,255,0.08)",
  },
  neutral: undefined,
};

const ICON_BOX: Record<Tone, string> = {
  safe: "border-accent-emerald/30 bg-accent-emerald/10",
  danger: "border-accent-rose/30 bg-accent-rose/10",
  neutral: "border-accent-amber/25 bg-accent-amber/10",
};

const ICON_COLOR: Record<Tone, string> = {
  safe: "text-accent-emerald",
  danger: "text-accent-rose",
  neutral: "text-accent-amber",
};

function OffPagePracticeSlide({ active }: SlideProps) {
  return (
    <SlideShell chapter={3} title="Off-page in practice (safely)">
      <div className="mx-auto w-full max-w-6xl">
        <RevealList active={active} stagger={0.14} className="grid grid-cols-2 gap-8">
          {PRACTICES.map(({ icon: Icon, tone, title, body }, i) => (
            <RevealItem key={i} className="h-full">
              <div
                className="glass flex h-full items-start gap-6 p-8 transition-transform duration-300 hover:scale-[1.02]"
                style={CARD_STYLE[tone]}
              >
                <span
                  className={`flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl border ${ICON_BOX[tone]}`}
                >
                  <Icon className={`h-8 w-8 ${ICON_COLOR[tone]}`} strokeWidth={1.75} />
                </span>
                <span className="block">
                  <span className="block font-display text-3xl font-semibold text-white">
                    {title}
                  </span>
                  <span className="mt-3 block text-2xl leading-relaxed text-white/90">
                    {body}
                  </span>
                </span>
              </div>
            </RevealItem>
          ))}
        </RevealList>

        <Reveal active={active} delay={1.1} className="mt-16 text-center">
          <p className="font-display text-3xl text-white/90">
            <GradientText className="font-semibold">Earn</GradientText> links —
            don&apos;t manufacture them.
          </p>
        </Reveal>
      </div>
    </SlideShell>
  );
}

export const offpagePractice: SlideDef = {
  id: "c3-offpage-practice",
  chapter: 3,
  title: "Off-page in practice (safely)",
  component: OffPagePracticeSlide,
};
